import { useMemo, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import RunSimulationButton from './RunSimulationButton';
import { effectiveConstantInputRate } from '../../lib/projectNormalization';
import type { GenerationProfile } from '../../types/nodeConfig';
import type { ScenarioPreset } from '../../types/scenario';

function productMixSummary(node: Record<string, unknown>): string {
  const types =
    (node.product_types as Array<{ name: string; percentage: number; enabled?: boolean }>) ??
    [];
  const enabled = types.filter((t) => t.enabled !== false);
  if (enabled.length === 0) return 'No product types';
  return enabled.map((t) => `${t.name} ${t.percentage}%`).join(', ');
}

function currentRate(node: Record<string, unknown>): number {
  return effectiveConstantInputRate(node);
}

function isBellCurve(node: Record<string, unknown>): boolean {
  const profile = node.generation_profile as GenerationProfile | undefined;
  return profile?.type === 'bell_curve';
}

export default function ScenarioControlPanel({
  readOnly = false,
}: {
  readOnly?: boolean;
}) {
  const document = useEditorStore((s) => s.document);
  const projectId = useEditorStore((s) => s.projectId);
  const scenarios = useSimulationStore((s) => s.scenarios);
  const activeScenarioId = useSimulationStore((s) => s.activeScenarioId);
  const liveOverrides = useSimulationStore((s) => s.liveOverrides);
  const loadScenarioPreset = useSimulationStore((s) => s.loadScenarioPreset);
  const saveScenarioPreset = useSimulationStore((s) => s.saveScenarioPreset);
  const deleteScenarioPreset = useSimulationStore((s) => s.deleteScenarioPreset);
  const setGlobalRateMultiplier = useSimulationStore((s) => s.setGlobalRateMultiplier);
  const setInputRateMultiplier = useSimulationStore((s) => s.setInputRateMultiplier);
  const setBellCurveAdjustments = useSimulationStore((s) => s.setBellCurveAdjustments);
  const persistScenarios = useSimulationStore((s) => s.persistScenarios);
  const setProcessorCapacity = useSimulationStore((s) => s.setProcessorCapacity);

  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const inputs = useMemo(
    () => document?.nodes.filter((n) => n.type === 'input') ?? [],
    [document],
  );

  const processors = useMemo(
    () => document?.nodes.filter((n) => n.type === 'processor') ?? [],
    [document],
  );

  const globalMult = liveOverrides.global_rate_multiplier ?? 1.0;

  const handleSaveCurrent = async () => {
    if (!newName.trim()) return;
    const preset: ScenarioPreset = {
      id: crypto.randomUUID(),
      name: newName.trim(),
      description: `Saved ${new Date().toLocaleString()}`,
      overrides: { ...liveOverrides },
    };
    saveScenarioPreset(preset);
    setNewName('');
    if (projectId && !readOnly) {
      setSaving(true);
      try {
        await persistScenarios(projectId);
      } finally {
        setSaving(false);
      }
    }
  };

  const handlePersist = async () => {
    if (!projectId || readOnly) return;
    setSaving(true);
    try {
      await persistScenarios(projectId);
    } finally {
      setSaving(false);
    }
  };

  const inputOverride = (nodeId: string) =>
    liveOverrides.input_overrides?.find((o) => o.node_id === nodeId);

  const processorOverride = (nodeId: string) =>
    liveOverrides.processor_overrides?.find((o) => o.node_id === nodeId);

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[10rem] flex-1">
          <label className="mb-1 block text-xs text-text-muted">Scenario preset</label>
          <select
            className="input-field w-full"
            value={activeScenarioId ?? ''}
            onChange={(e) => {
              const preset = scenarios.find((s) => s.id === e.target.value);
              if (preset) loadScenarioPreset(preset);
            }}
          >
            <option value="">Custom (unsaved)</option>
            {scenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <RunSimulationButton projectId={projectId} disabled={readOnly} />
      </div>

      <p className="text-xs text-text-muted">
        Click <strong>Run simulation</strong> to choose duration (including run
        until stopped) and canvas refresh interval.
      </p>

      <div className="rounded-lg border border-border bg-surface/40 p-3">
        <h3 className="panel-section-title mb-2">Global flow multiplier</h3>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0.5}
            max={1.5}
            step={0.05}
            className="flex-1"
            disabled={readOnly}
            value={globalMult}
            onChange={(e) => setGlobalRateMultiplier(Number(e.target.value))}
          />
          <span className="w-14 text-right text-sm font-medium text-text">
            {Math.round(globalMult * 100)}%
          </span>
        </div>
      </div>

      <div>
        <h3 className="panel-section-title mb-2">Input nodes</h3>
        {inputs.length === 0 ? (
          <p className="text-sm text-text-muted">No input nodes in this project.</p>
        ) : (
          <div className="space-y-2">
            {inputs.map((node) => {
              const ov = inputOverride(node.id);
              const perMult = ov?.rate_multiplier ?? 1.0;
              const effective = currentRate(node) * globalMult * perMult;
              return (
                <div
                  key={node.id}
                  className="rounded-lg border border-border bg-surface/30 p-3"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="font-medium text-text">{node.name}</span>
                      <span className="ml-2 text-xs text-text-muted">
                        {effective.toFixed(1)}/min effective
                      </span>
                    </div>
                    <span className="text-xs text-text-subtle">
                      {productMixSummary(node)}
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-[10px] text-text-muted">
                        Per-input multiplier
                      </label>
                      <input
                        type="range"
                        min={0.5}
                        max={1.5}
                        step={0.05}
                        className="w-full"
                        disabled={readOnly}
                        value={perMult}
                        onChange={(e) =>
                          setInputRateMultiplier(node.id, Number(e.target.value))
                        }
                      />
                      <span className="text-[10px] text-text-subtle">
                        {Math.round(perMult * 100)}%
                      </span>
                    </div>
                    {isBellCurve(node) && (
                      <div className="space-y-1">
                        <label className="block text-[10px] text-text-muted">
                          Bell curve shift
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            className="input-field flex-1 py-1 text-xs"
                            placeholder="Peak shift (s)"
                            disabled={readOnly}
                            value={ov?.bell_curve?.peak_time_shift_seconds ?? 0}
                            onChange={(e) =>
                              setBellCurveAdjustments(node.id, {
                                peak_time_shift_seconds: Number(e.target.value),
                              })
                            }
                          />
                          <input
                            type="number"
                            min={0.5}
                            max={2}
                            step={0.1}
                            className="input-field w-20 py-1 text-xs"
                            placeholder="Spread"
                            disabled={readOnly}
                            value={ov?.bell_curve?.spread_multiplier ?? 1}
                            onChange={(e) =>
                              setBellCurveAdjustments(node.id, {
                                spread_multiplier: Number(e.target.value),
                              })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {processors.length > 0 && (
        <div>
          <h3 className="panel-section-title mb-2">Processor capacity overrides</h3>
          <div className="space-y-2">
            {processors.map((node) => {
              const baseCapacity = Number(node.capacity ?? 1);
              const ov = processorOverride(node.id);
              const effective = ov?.capacity ?? baseCapacity;
              return (
                <div
                  key={node.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface/30 p-3"
                >
                  <div>
                    <span className="font-medium text-text">{node.name}</span>
                    <span className="ml-2 text-xs text-text-muted">
                      base {baseCapacity}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-text-muted">Capacity</label>
                    <input
                      type="number"
                      min={1}
                      max={32}
                      className="input-field w-16 py-1 text-xs"
                      disabled={readOnly}
                      value={effective}
                      onChange={(e) =>
                        setProcessorCapacity(node.id, Number(e.target.value))
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <input
            type="text"
            className="input-field min-w-[10rem] flex-1"
            placeholder="New preset name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button
            type="button"
            className="btn-secondary text-xs"
            disabled={!newName.trim() || saving}
            onClick={() => void handleSaveCurrent()}
          >
            Save preset
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={saving}
            onClick={() => void handlePersist()}
          >
            Sync to project
          </button>
          {activeScenarioId && (
            <button
              type="button"
              className="btn-danger text-xs"
              onClick={() => deleteScenarioPreset(activeScenarioId)}
            >
              Delete preset
            </button>
          )}
        </div>
      )}
    </div>
  );
}
